import React, { useCallback } from "react"
import { SchemaFormRefProps } from "resha"
import { SchemaForm } from "universal-schema/lib"
import { Button } from "dash-styled"

import { Banner } from "../../../../../../../../Common/Banner/Banner"
import { LinkButton } from "../../../../../../../../Common/LinkButton/LinkButton"
import { downloadAsJSON } from "../../../../../../../../../Helpers/Functions/downloadAs"
import { hasValidAppParams } from "../../../../../../../../../Models/API/AppInstanceModel"
import { InputFormRender } from "../../../../../../../../Common/InputFormRender/InputFormRender"
import { DocumentsExtraction } from "../../../../../../../../Common/SchemaForm/DocumentsExtraction"
import { APIAppInstanceState } from "../../../../../../../../../Reducers/API/AppInstance/AppInstanceReducer"
import { APIAppTemplateModel } from "../../../../../../../../../Models/API/AppTemplateModel"
import {
    DocumentsExtractionSettings,
    ExtraSettingsProps
} from "./DocumentsExtraction"

type JobSettingsProps = {
    appID: string
    disabled: boolean
    noSave: boolean
    saving: boolean
    instance: APIAppInstanceState
    template: APIAppTemplateModel
    schemaForm: SchemaForm
    formRef: React.RefObject<SchemaFormRefProps>
    onSubmit: (formData: unknown) => void
    onChange: (formData: unknown) => void
    formData: any
    liveValidationState: [boolean, () => void]
}

export const JobSettings = ({
    appID,
    disabled,
    noSave,
    saving,
    instance,
    template,
    schemaForm,
    formRef,
    onSubmit,
    onChange,
    formData,
    liveValidationState: [liveValidateForm, startLiveValidatingForm]
}: JobSettingsProps) => {
    const isValid = hasValidAppParams(instance, template)

    const handleError = useCallback(
        () => startLiveValidatingForm(),
        [startLiveValidatingForm]
    )

    const handleDownloadAppParams = () =>
        downloadAsJSON(instance.appParams?.params ?? {}, "task_design")

    const handleSubmit = () => {
        formRef.current?.submit()
    }

    if (
        schemaForm.uiSchema["ui:field"] ===
        "https://widgets.super.ai/document-extraction-parameter@1"
    ) {
        return (
            <>
                {!isValid && (
                    <Banner icon="info" className="w-full mb-6">
                        Current job design is out-of-sync with the data program.
                        If it isn't shown below, download as JSON from{" "}
                        <LinkButton onClick={handleDownloadAppParams}>
                            this link
                        </LinkButton>
                        .
                    </Banner>
                )}
                <DocumentsExtraction<ExtraSettingsProps>
                    submitting={saving}
                    parameterSchema={schemaForm}
                    output={{
                        ref: formRef,
                        formData,
                        liveValidate: liveValidateForm,
                        onError: handleError,
                        onChange,
                        onSubmit
                    }}
                    children={DocumentsExtractionSettings}
                    onForward={handleSubmit}
                    appID={appID}
                    noSave={noSave}
                />
            </>
        )
    }

    return (
        <>
            {!isValid && (
                <Banner icon="info" className="w-full mb-6">
                    Current job design is out-of-sync with the data program. If
                    it isn't shown below, download as JSON from{" "}
                    <LinkButton onClick={handleDownloadAppParams}>
                        this link
                    </LinkButton>
                    .
                </Banner>
            )}
            {!disabled ? (
                <>
                    <InputFormRender
                        testId="parameters-form"
                        ref={formRef}
                        {...schemaForm}
                        formData={formData}
                        onChange={onChange}
                        onSubmit={onSubmit}
                        className="w-full"
                        liveValidate={liveValidateForm}
                        shouldRenderFieldInPanel={
                            schemaForm.uiSchema["ui:field"] !==
                            "https://widgets.super.ai/document-extraction-parameter@1"
                        }
                        onError={handleError}
                    />
                    <Button
                        color="darkviolet"
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={noSave || saving}
                        testId="apply-parameters"
                        className="my-2 w-auto px-12"
                    >
                        {saving ? "Saving..." : "Save changes"}
                    </Button>
                </>
            ) : (
                <InputFormRender
                    className="w-full"
                    {...schemaForm}
                    formData={instance.appParams?.params}
                    shouldRenderFieldInPanel={
                        schemaForm.uiSchema["ui:field"] !==
                        "https://widgets.super.ai/document-extraction-parameter@1"
                    }
                    skipMissingDataField={true}
                />
            )}
        </>
    )
}
